import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import {
  formatKES,
  formatDate,
  currentMonth,
  tenantBalance,
  computeTenantStatus,
  tenantNextDueDate,
  today,
  utilityChargeForMonth,
  utilityBalanceForMonth,
  totalMonthlyDue,
  totalBalanceForMonth,
} from "../utils/format";
import { MessageSquare, Loader2, Info, CheckCircle2, XCircle } from "lucide-react";

const TEMPLATES = {
  all: "Hi {name}, this is a friendly reminder that your rent of {amount} for house {house} is due. Please arrange payment at your earliest convenience. Thank you.",
  overdue:
    "Hi {name}, your rent of {amount} for house {house} is now OVERDUE. A balance of {balance} remains outstanding. Kindly settle immediately to avoid penalties. Thank you.",
  partial:
    "Hi {name}, thank you for the partial payment on house {house}. A balance of {balance} is still outstanding. Please complete payment when you can. Thank you.",
  pending:
    "Hi {name}, this is a reminder that rent of {amount} for house {house} is due on {dueDate}. Please prepare to settle on time. Thank you.",
};

// Replaces all placeholders per tenant including utility amounts.
function renderMessage(template, tenant, payments, utilityCharges, month) {
  const balance = tenantBalance(tenant, payments, month);
  const amount = Number(tenant.rent) || 0;
  const nextDueIso = tenantNextDueDate(tenant, payments);
  const dueDate =
    nextDueIso === today()
      ? `${formatDate(nextDueIso)} (today)`
      : formatDate(nextDueIso);
  const waterAmt = utilityChargeForMonth(utilityCharges, tenant.id, month, "water");
  const waterBal = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "water");
  const elecAmt = utilityChargeForMonth(utilityCharges, tenant.id, month, "electricity");
  const elecBal = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "electricity");
  const totalDue = totalMonthlyDue(tenant, utilityCharges, month);
  const totalBal = totalBalanceForMonth(tenant, utilityCharges, payments, month);
  return String(template)
    .replaceAll("{name}", tenant.name || "")
    .replaceAll("{house}", tenant.houseNumber || "")
    .replaceAll("{amount}", formatKES(amount))
    .replaceAll("{balance}", formatKES(balance))
    .replaceAll("{dueDate}", dueDate)
    .replaceAll("{waterAmount}", formatKES(waterAmt))
    .replaceAll("{waterBalance}", formatKES(waterBal))
    .replaceAll("{electricityAmount}", formatKES(elecAmt))
    .replaceAll("{electricityBalance}", formatKES(elecBal))
    .replaceAll("{totalDue}", formatKES(totalDue))
    .replaceAll("{totalBalance}", formatKES(totalBal));
}

export default function BulkSmsDialog({ open, onClose, tenants = [] }) {
  const { payments, utilityCharges, sendBulkSms } = useData();
  const month = currentMonth();
  const [template, setTemplate] = useState(TEMPLATES.all);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState("all");
  // null = not sent, { queued: N, failed: N, total: N }
  const [result, setResult] = useState(null);
  const [excluded, setExcluded] = useState(new Set());

  useEffect(() => {
    if (open) {
      setTemplate(TEMPLATES.all);
      setTarget("all");
      setResult(null);
      setExcluded(new Set());
    }
  }, [open]);

  // Auto-swap the template when the target changes, but only if the user
  // hasn't customised it (i.e. it still matches one of the built-in templates).
  const changeTarget = (next) => {
    setTarget(next);
    setExcluded(new Set());
    const isStillDefault = Object.values(TEMPLATES).includes(template);
    if (isStillDefault) setTemplate(TEMPLATES[next] || TEMPLATES.all);
  };

  // Narrow recipient list by status filter within the provided tenants.
  const recipients = useMemo(() => {
    return tenants.filter((t) => {
      if (t.status !== "ACTIVE") return false;
      if (target === "all") return true;
      const s = computeTenantStatus(t, payments, month).toLowerCase();
      return s === target;
    });
  }, [tenants, target, payments, month]);

  const countsByStatus = useMemo(() => {
    const active = tenants.filter((t) => t.status === "ACTIVE");
    const out = { all: active.length, overdue: 0, partial: 0, pending: 0, paid: 0 };
    active.forEach((t) => {
      const s = computeTenantStatus(t, payments, month).toLowerCase();
      if (out[s] != null) out[s]++;
    });
    return out;
  }, [tenants, payments, month]);

  const selected = recipients.filter((t) => !excluded.has(t.id));

  const toggleOne = (id) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (excluded.size === 0) {
      setExcluded(new Set(recipients.map((t) => t.id)));
    } else {
      setExcluded(new Set());
    }
  };

  const preview = selected[0]
    ? renderMessage(template, selected[0], payments, utilityCharges, month)
    : template;

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (selected.length === 0 || !template.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const messages = selected.map((t) => ({
        tenantId: t.id,
        message: renderMessage(template, t, payments, utilityCharges, month),
      }));
      const data = await sendBulkSms(messages);
      const queued = data.queued != null ? data.queued : (data.communications?.length || messages.length);
      setResult({ queued, failed: 0, total: data.total || queued });
    } catch (err) {
      console.error("Bulk SMS failed:", err);
      setResult({ queued: 0, failed: recipients.length, total: recipients.length });
    }
    setLoading(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk SMS reminders"
      description={result ? "Delivery summary" : `Send a message to ${selected.length} tenant${selected.length === 1 ? "" : "s"}.`}
    >
      {result ? (
        <div className="space-y-4">
          {result.failed === 0 ? (
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4">
              <CheckCircle2 className="h-5 w-5 flex-none text-green-600 dark:text-green-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                  {result.queued} message{result.queued === 1 ? "" : "s"} queued for delivery
                </p>
                <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                  Messages are being sent in the background. You'll see them update in each tenant's history as they're delivered.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4">
              <XCircle className="h-5 w-5 flex-none text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">SMS sending failed</p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  Could not queue messages for {result.total} recipient{result.total === 1 ? "" : "s"}. Please try again.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={handleClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label-base">Target</label>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "all", l: "All active", c: countsByStatus.all },
              { v: "overdue", l: "Overdue", c: countsByStatus.overdue },
              { v: "partial", l: "Partial", c: countsByStatus.partial },
              { v: "pending", l: "Pending", c: countsByStatus.pending },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => changeTarget(o.v)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                  target === o.v
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {o.l} · {o.c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-base">Message template</label>
          <textarea
            className="input-base min-h-[110px] resize-y font-mono text-xs"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            required
          />
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-gray-500">
            <Info className="mt-px h-3 w-3 flex-none" />
            <span>
              Placeholders: <code className="font-mono">{"{name}"}</code>,{" "}
              <code className="font-mono">{"{house}"}</code>,{" "}
              <code className="font-mono">{"{amount}"}</code>,{" "}
              <code className="font-mono">{"{balance}"}</code>,{" "}
              <code className="font-mono">{"{dueDate}"}</code>,{" "}
              <code className="font-mono">{"{waterAmount}"}</code>,{" "}
              <code className="font-mono">{"{waterBalance}"}</code>,{" "}
              <code className="font-mono">{"{electricityAmount}"}</code>,{" "}
              <code className="font-mono">{"{electricityBalance}"}</code>,{" "}
              <code className="font-mono">{"{totalDue}"}</code>,{" "}
              <code className="font-mono">{"{totalBalance}"}</code>
            </span>
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Preview
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-sm text-gray-900 dark:text-white">
            {recipients.length === 0 ? (
              <span className="text-gray-400">No recipients match this target.</span>
            ) : (
              preview
            )}
          </div>
        </div>

        {recipients.length > 0 && (
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Recipients ({selected.length} of {recipients.length})
              </span>
              <button type="button" onClick={toggleAll} className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                {excluded.size === 0 ? "Deselect all" : "Select all"}
              </button>
            </div>
            <ul className="max-h-40 divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              {recipients.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => toggleOne(t.id)}
                >
                  <input
                    type="checkbox"
                    checked={!excluded.has(t.id)}
                    onChange={() => toggleOne(t.id)}
                    className="checkbox-base"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="flex-1 min-w-0 truncate font-medium text-gray-900 dark:text-white">
                    {t.name}
                    {t.houseNumber && (
                      <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400">· House {t.houseNumber}</span>
                    )}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">{t.phone || "no phone"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || selected.length === 0}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MessageSquare className="h-4 w-4" />
            )}
            Send {selected.length} SMS
          </button>
        </div>
      </form>
      )}
    </Modal>
  );
}
